import type {
  Bonus,
  Differentiator,
  Faq,
  InsideImage,
  Module,
  PainPoint,
  PopupSale,
  Testimonial,
} from '../types/content'

export const PRODUCT_NAME = 'POOL & SPA DIAGNOSTIC PRO™'
export const CHECKOUT_URL = 'https://pool-spa-diagnostic-pro.impultienda.ar/checkout'
export const PURCHASE_ANCHOR = '#offer'
export const META_PIXEL_ID = '1514417324213827'

export const BOX_IMAGE =
  'https://cdn.magicpatterns.com/uploads/nGY2axQfuQYQMpha6pbKFC/Diseno_sin_titulo-17.webp'

export const diagnosticFlow = ['IDENTIFY', 'INSPECT', 'DIAGNOSE', 'ISOLATE', 'REPAIR', 'VERIFY']

export const insideImages: InsideImage[] = [
  {
    src: 'https://cdn.magicpatterns.com/uploads/dXKkQsnp9T9ArUjJo6ECFU/Captura_de_pantalla_2026-10-08_a_la(s)_8.48.16_p._m..png',
    alt: 'Bearing Failure Symptoms & Mechanical Condition — motor cutaway, condition checklist and decision framework',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/9CY6tmqXSxro9gr89J5qMg/Captura_de_pantalla_2026-10-08_a_la(s)_8.47.42_p._m..png',
    alt: 'Locate the Problem by System Side — suction side vs pressure side diagram with symptom table',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/p2pBLEaTjrQdg91xLkBcnP/Captura_de_pantalla_2026-10-08_a_la(s)_8.47.30_p._m..png',
    alt: 'Know the Parts Before Diagnosing the Failure — exploded pump diagram with component table',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/rY7xiPnhLnSJhBZfkV16kP/Captura_de_pantalla_2026-10-08_a_la(s)_8.47.19_p._m..png',
    alt: 'How Pool & Spa Water Circulation Works — circulation diagram with component functions',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/5SiQDURX4f8T2SQ6Arud9n/Captura_de_pantalla_2026-10-08_a_la(s)_8.47.55_p._m..png',
    alt: 'Intermittent Prime Loss & Air Accumulation — air-entry points, worksheet and decision tree',
  },
]

export const painPoints: PainPoint[] = [
  {
    title: '😰 TOO MANY POSSIBLE CAUSES. TOO MUCH TIME TROUBLESHOOTING.',
    body: 'Low water flow? Pump not priming? Heater shutting down? Filter pressure problems? Air leaks? Motor overheating? Without a structured diagnostic process, identifying the actual cause can turn into hours of unnecessary checks and repeated testing.',
  },
  {
    title: "⚠️ YOU FOUND THE SYMPTOM—BUT WHAT'S ACTUALLY FAILING?",
    body: "A pump losing pressure doesn't automatically mean the pump is defective. A heater shutting down doesn't always mean the heater needs replacing. Without systematic fault isolation, technicians can waste time investigating the wrong components.",
  },
  {
    title: '📚 YOUR TECHNICAL INFORMATION IS EVERYWHERE',
    body: "Equipment manuals, troubleshooting charts, error codes, inspection notes, repair procedures, maintenance records and customer complaints. The information may exist—but it's scattered when you need to identify a fault and make a repair decision.",
  },
  {
    title: '😩 STOP TROUBLESHOOTING EVERY EQUIPMENT FAILURE FROM SCRATCH',
    body: "Moving from one possible cause to another without a clear process isn't efficient troubleshooting. Approach every service call with organized symptom identification, equipment checks, diagnostic workflows and repair verification instead.",
  },
]

export const modules: Module[] = [
  {
    number: 1,
    title: 'POOL & SPA EQUIPMENT FUNDAMENTALS & SYSTEM DIAGNOSTICS™',
    body: 'Understand pool and spa equipment with practical component identification guides, system diagrams, circulation references, inspection checklists and structured assessment tools covering pumps, filters, heaters, valves, plumbing and essential equipment connections.',
  },
  {
    number: 2,
    title: 'PUMPS, MOTORS & WATER CIRCULATION TROUBLESHOOTING™',
    body: 'Diagnose pump priming failures, low water flow, air leaks, suction restrictions, clogged impellers, worn seals, motor overheating, unusual vibration and circulation problems using symptom charts, equipment references and step-by-step fault-isolation workflows.',
  },
  {
    number: 3,
    title: 'FILTRATION, WATER QUALITY & PLUMBING DIAGNOSTICS™',
    body: 'Investigate filtration failures, abnormal pressure readings, cloudy water, sanitation problems, plumbing leaks, faulty valves, blocked lines and water circulation issues with practical diagnostic tables, inspection guides and corrective-action references.',
  },
  {
    number: 4,
    title: 'HEATERS, HEAT PUMPS & TEMPERATURE CONTROL DIAGNOSTICS™',
    body: 'Identify heating system problems involving gas heaters, electric heaters, heat pumps, temperature sensors, thermostats, flow switches, shutdowns and inconsistent temperatures using equipment diagrams, fault-reference tools and structured troubleshooting procedures.',
  },
  {
    number: 5,
    title: 'ELECTRICAL CONTROLS, AUTOMATION & SPA SYSTEM TROUBLESHOOTING™',
    body: 'Recognize control panel problems, timer failures, automation errors, unexpected shutdowns, communication faults, spa jet malfunctions and blower issues using component references, safety checklists, diagnostic tables and fault-isolation decision trees.',
  },
  {
    number: 6,
    title: 'COMPLETE FAULT-FINDING, REPAIR WORKFLOWS & SERVICE PROCEDURES™',
    body: 'Move from customer complaints to organized repair decisions with symptom-to-cause matrices, diagnostic decision trees, equipment inspection forms, repair planning worksheets, service documentation templates and post-repair verification checklists.',
  },
]

export const bonuses: Bonus[] = [
  {
    number: 1,
    title: 'POOL & SPA SERVICE INSPECTION TOOLKIT™',
    body: 'Conduct more organized service inspections with ready-to-use equipment checklists, condition assessment forms, inspection records, findings registers, customer reports and service verification templates.',
  },
  {
    number: 2,
    title: 'POOL & SPA FAULT CODE REFERENCE GUIDE™',
    body: 'Interpret equipment warnings more systematically with fault-category references, error documentation worksheets, manufacturer manual verification tools, diagnostic decision trees and repeated-fault tracking records.',
  },
  {
    number: 3,
    title: 'POOL & SPA WATER TESTING & CORRECTION TOOLKIT™',
    body: 'Organize water quality testing with practical result logs, parameter interpretation worksheets, corrective-action planning tools, treatment documentation forms and follow-up verification records.',
  },
  {
    number: 4,
    title: 'POOL & SPA REPAIR ESTIMATING & JOB DOCUMENTATION KIT™',
    body: 'Manage repair jobs professionally with estimate templates, labor and materials worksheets, customer authorization forms, work orders, service reports, invoice templates and repair completion records.',
  },
  {
    number: 5,
    title: 'POOL & SPA PREVENTIVE MAINTENANCE PLANNER™',
    body: 'Plan recurring maintenance with equipment service schedules, preventive maintenance checklists, seasonal planning worksheets, service history logs, follow-up trackers and customer maintenance summaries.',
  },
]

export const differentiators: Differentiator[] = [
  {
    emoji: '🔧',
    title: 'Practical troubleshooting resources',
    body: 'Find the equipment reference, diagnostic chart, inspection checklist, fault-isolation workflow or repair documentation tool you need.',
  },
  {
    emoji: '🔍',
    title: 'Structured diagnostic process',
    body: 'Move from identifying equipment symptoms to inspecting components, isolating potential faults, planning corrective actions and verifying repairs.',
  },
  {
    emoji: '⚙️',
    title: '6 dedicated diagnostic areas',
    body: 'Equipment fundamentals, pumps and circulation, filtration and plumbing, heating systems, electrical controls and complete repair workflows.',
  },
  {
    emoji: '🎁',
    title: '5 practical professional bonuses',
    body: 'Service inspections, fault-code references, water testing, repair estimating and preventive maintenance planning tools.',
  },
  {
    emoji: '🔄',
    title: 'Ready to use again and again',
    body: 'Return to the system whenever you need to investigate equipment problems, organize a repair, document service findings or plan maintenance.',
  },
]

export const offerBullets = [
  '6 comprehensive diagnostic & repair modules',
  '5 strategic professional bonuses',
  'Pool & spa equipment identification resources',
  'Pump, motor & circulation troubleshooting guides',
  'Filtration, plumbing & water quality diagnostics',
  'Heater, heat pump & temperature control references',
  'Electrical controls & spa equipment fault workflows',
  'Repair documentation & preventive maintenance tools',
]

export const testimonials: Testimonial[] = [
  {
    name: 'Jason Miller, 42',
    role: 'Pool Service Technician',
    location: 'Tampa, United States',
    headline: 'Troubleshooting equipment feels much more organized.',
    quote:
      'Instead of checking different components without a clear direction, I can follow a structured process from the reported symptom to the equipment inspection and next diagnostic step. It makes service calls easier to organize.',
  },
  {
    name: 'Craig Thompson, 48',
    role: 'Pool Equipment Technician',
    location: 'Gold Coast, Australia',
    headline: 'The pump troubleshooting resources are extremely useful.',
    quote:
      'Low-flow complaints and pump problems can have several possible causes. Having the symptoms, components and inspection points organized together makes it easier to work through a problem systematically.',
  },
  {
    name: 'Laura Bennett, 39',
    role: 'Pool Maintenance Professional',
    location: 'Kelowna, Canada',
    headline: 'Much better than scattered manuals and notes.',
    quote:
      'I used to switch between equipment manuals, service notes and different troubleshooting references. Having the diagnostic process organized in one system makes it much easier to find the information I need.',
  },
  {
    name: 'Mark Davies, 51',
    role: 'Pool & Spa Repair Technician',
    location: 'Brighton, United Kingdom',
    headline: 'The repair workflows make a real difference.',
    quote:
      'Identifying a possible problem is only part of the job. I like having a clear way to document findings, organize corrective actions and verify equipment operation after completing service work.',
  },
  {
    name: 'Ryan Cooper, 36',
    role: 'Pool & Spa Service Professional',
    location: 'Scottsdale, United States',
    headline: 'A practical resource I can keep using.',
    quote:
      "This isn't something I would read once and forget. The inspection checklists, equipment references, diagnostic workflows and service forms are resources I can return to whenever I'm working on a pool or spa system.",
  },
]

export const faqs: Faq[] = [
  {
    q: 'Who is this system for?',
    a: 'Pool and spa service technicians, equipment repair professionals, maintenance contractors, pool service businesses and other qualified professionals involved in troubleshooting and maintaining pool and spa equipment.',
  },
  {
    q: 'What types of equipment problems does it cover?',
    a: 'The system includes practical diagnostic resources for pump failures, motor problems, circulation restrictions, filtration issues, plumbing leaks, heater malfunctions, temperature control faults, automation problems and common spa equipment issues.',
  },
  {
    q: 'Does it include ready-to-use troubleshooting guides?',
    a: 'Yes. Equipment inspection checklists, symptom-to-cause charts, fault-isolation decision trees, diagnostic workflows and repair documentation resources are central parts of the system.',
  },
  {
    q: 'Does it help after an equipment fault has been identified?',
    a: 'Yes. The system includes resources for organizing repair decisions, documenting findings, planning corrective actions, recording service work and verifying equipment operation.',
  },
  {
    q: 'Can I use it for both pool and spa equipment?',
    a: 'Yes. The resources cover pool and spa systems, including pumps, filters, heaters, heat pumps, plumbing, electrical controls, automation and common spa components such as jets and blowers.',
  },
  {
    q: 'Is this a physical product?',
    a: 'No. POOL & SPA DIAGNOSTIC PRO™ is a 100% digital resource.',
  },
  {
    q: 'Is this a training course or certification?',
    a: 'No. It is a professional diagnostic and reference system designed to support structured pool and spa equipment troubleshooting and repair activities.',
  },
  {
    q: 'Is it another theory-heavy repair ebook?',
    a: 'No. The system focuses on equipment references, diagnostic workflows, troubleshooting charts, inspection checklists, repair planning tools, service documentation and practical maintenance resources.',
  },
  {
    q: 'Does this replace professional qualifications or manufacturer procedures?',
    a: 'No. Equipment requirements vary by manufacturer, installation and jurisdiction. Applicable safety regulations, manufacturer instructions and qualified professional judgment must always be followed.',
  },
  {
    q: 'How will I receive it?',
    a: "You'll receive immediate digital access after purchase.",
  },
]

export const popupSales: PopupSale[] = [
  { name: 'Jason M.', city: 'Tampa', country: 'United States' },
  { name: 'Mark D.', city: 'Brighton', country: 'United Kingdom' },
  { name: 'Laura B.', city: 'Kelowna', country: 'Canada' },
  { name: 'Craig T.', city: 'Gold Coast', country: 'Australia' },
  { name: 'Kevin R.', city: 'Phoenix', country: 'United States' },
  { name: 'Oliver H.', city: 'Bristol', country: 'United Kingdom' },
  { name: 'Ethan P.', city: 'Mississauga', country: 'Canada' },
  { name: 'Sarah W.', city: 'Perth', country: 'Australia' },
  { name: 'Derek S.', city: 'Las Vegas', country: 'United States' },
  { name: 'James K.', city: 'Leeds', country: 'United Kingdom' },
  { name: 'Tyler N.', city: 'Kelowna', country: 'Canada' },
  { name: 'Ben L.', city: 'Sydney', country: 'Australia' },
]
